import { useEffect } from "react";
import type { AttendeeNotificationSetting, AttendeeSession } from "@verity-connect/contracts";
import { readClientId } from "./personal";

/**
 * Two facts about this phone that only this phone knows.
 *
 * Both close a gap the server cannot see from its own records, and both are the same shape
 * of failure: everything worked and the thing did not happen.
 *
 * Which schedule this phone is showing. The guide is served with a revision, and a phone
 * holding a saved copy sends the revision from whenever it saved. This product has already
 * served a stale schedule for a fortnight — loading perfectly, erroring never — and a
 * comparison like this one was the only thing that could have known.
 *
 * And whether notifications are switched on. Somebody who was asked and said no leaves no
 * subscription anywhere, so from the server they look exactly like somebody who never opened
 * the app, and only one of the two can be helped.
 *
 * Mounted at the root rather than on the notifications screen, on purpose. Reporting from
 * the screen where a person changes the setting only ever hears from people who went looking
 * for it — which is a small and unrepresentative part of a convention, and not the part that
 * is quietly unreachable.
 *
 * Nothing here can fail in a way anybody sees. Every report is fire-and-forget, and a report
 * that does not arrive costs a number on a board rather than anything a teacher is doing.
 */
export function useServiceReports(session: AttendeeSession | null, eventSlug: string, revision: string): void {
  useEffect(() => {
    if (!session) return;
    const clientId = readClientId();
    const base = `/api/attendee/events/${encodeURIComponent(eventSlug)}`;
    void post(`${base}/guide-report`, { clientId, revision });
    /*
     * The notification setting is reported only by a browser that is actually a browser.
     *
     * Inside the installed application this page is a web view, and a web view has no
     * Notification API at all — so it answered "unsupported" and filed it under "web", for
     * fifty iPhones that have the application installed and were perfectly reachable. Fifty
     * rows saying a browser cannot do notifications, about phones that are not browsers.
     *
     * The shell knows its own answer and sends it as "apple"; nothing here can improve on
     * that, and guessing alongside it only makes the true answer harder to find.
     */
    if (!insideTheApplication()) {
      void post(`${base}/push/state`, { clientId, platform: "web", setting: notificationSetting() });
    }
    // Reported once per mount, and again whenever the revision changes — which is exactly
    // when a phone has just picked up a newer schedule, and the most useful moment to say so.
  }, [session?.identityId, eventSlug, revision]);

  useEffect(() => {
    if (!session) return;
    const path = `/api/attendee/events/${encodeURIComponent(eventSlug)}/foreground`;
    const report = () => {
      if (document.visibilityState === "visible" && navigator.onLine) void post(path);
    };
    report();
    const timer = window.setInterval(report, 30_000);
    const visible = () => report();
    window.addEventListener("focus", visible);
    window.addEventListener("online", visible);
    window.addEventListener("pageshow", visible);
    window.addEventListener("wvcea:native-foreground", visible);
    document.addEventListener("visibilitychange", visible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", visible);
      window.removeEventListener("online", visible);
      window.removeEventListener("pageshow", visible);
      window.removeEventListener("wvcea:native-foreground", visible);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [session?.identityId, eventSlug]);
}

/**
 * Whether this page is running inside the installed application.
 *
 * The Worker marks the document it serves with `data-shell` when the request came from
 * inside one, which is server-side and in the markup before the browser sees it — the
 * injected script this replaced failed silently for a fortnight.
 */
function insideTheApplication(): boolean {
  return Boolean(document.documentElement.dataset.shell);
}

/**
 * What this browser would say if the board asked it directly.
 *
 * "default" is the browser's word for never having been asked, and separating it out is the
 * whole point: never asked is one prompt away from reachable, and refused is a person to
 * speak to at the door.
 */
function notificationSetting(): AttendeeNotificationSetting {
  if (!("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "allowed";
  if (Notification.permission === "denied") return "refused";
  return "not_asked";
}

async function post(path: string, body?: unknown): Promise<void> {
  try {
    await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      ...(body === undefined ? {} : { headers: { "content-type": "application/json" }, body: JSON.stringify(body) }),
    });
  } catch {
    // Nothing. Somebody is using the convention; we simply cannot count this one.
  }
}
